/**
 * ES6 module
 * Form helper module
 * A helper module that initially will contain abstracted method for cs-forms
 * ©2020 CoreStream Ltd
 *
 * @format
 */

/* eslint no-console: 0, eqeqeq: 0 */

/*
ES6 modules should conform to the following rules
i) They should not contain permament references to external objects - i.e. event listeners/DOM nodes/Vue reactive data - $cs_.deepClone() Objects to ensure pointers are not sent
ii) Passing temporary references as parameters is acceptable but be careful not to create closures
iii) ES6 module declared objects and properties must be mutated inside the module through custom module function calls
*/

//IMPORTS
import $cs_ from '@/js/plugins/cs-underscore-helpers';

//PRIVATE PROPERTIES
const $_formGUIDs = []; //form model
const $_formSettings = {}; //form model

const $_formDirty = {}; // form validation model object
const $_formStateModel = {}; //form mounted model
const $_dualModeFormsState = new Map();

let $_currentGUID = 100;

//PUBLIC PROPERTIES

//PRIVATE METHODS - underscore namespace

const $_csForm = {};


//PUBLIC METHODS - note there is no "this" context - when calling internal methods 

export const $csForm = {
	/**
	 * @public
	 * @description
	 * creates a new form GUID instance and returns that to the caller
	 * @returns {String}
	 */
	createNewFormGuid: () => {
		$_currentGUID += 5;
		let guidString = $_currentGUID.toString();
		$_formGUIDs.push(guidString);
		return guidString;
	},
	
	/**
	 * @public
	 * @description
	 * sets the formSettings model
	 * @param {String} GUID
	 * @param {Object} settingsObject
	 * @returns {Object}
	 */
	setFormSettingsObject: (GUID, settingsObject) => {
		if (GUID != undefined && GUID !== '' && settingsObject != undefined && typeof settingsObject === 'object' && Object.keys(settingsObject).length > 0) {
			//normalize mode
			settingsObject.mode = settingsObject.mode.toLowerCase();

			$_formSettings[GUID] = $cs_.deepClone(settingsObject, {freeze: true});
			return $_formSettings[GUID];
		} else {
			return {
				id: '',
				itemId: '',
				mode: '',
				collectionId: '',
				title: '',
				editorOption: []
			};
		}
	},

	/**
	 * @public
	 * @description
	 * mutates the $_formSettings object setting data to the given ...path array in the object
	 * TODO - currently this will unfreeze the Object - need to consider how best to refreeze it???
	 * @param {Mixed} data
	 * @param {Mixed} ...path (spread operator gives and Array - path should be Numbers and/or Strings)
	 */
	mutateFormSettingsObject: (data, ...path) => {
		let i = 1,
			len = path.length - 1;
		if (path.length !== 0 && $_formSettings[path[0]] != undefined) {
			//unfreeze the object
			if (Object.isFrozen($_formSettings[path[0]]) === true) $_formSettings[path[0]] = $cs_.deepClone($_formSettings[path[0]]);
			let obj = $_formSettings[path[0]];
			while (i < len) {
				obj = obj[path[i]];
				i++;
			}
			obj[path[i]] = data;
		}
	},

	/**
	 * @public
	 * @description
	 * returns the $_formSettings object - if found - defaults to custom object
	 * @returns {Object}
	 */
	getFormSettingsObject: (GUID) => {
		if ($_formSettings[GUID] != undefined && typeof $_formSettings[GUID] === 'object') return $_formSettings[GUID];
		else {
			return {
				id: '',
				itemId: '',
				mode: '',
				collectionId: '',
				title: '',
				editorOption: []
			};
		}
	},
	/**
	 * @public
	 * @description
	 * Return the GUID of the first form opened.
	 */
	getFirstFormGuidOpened() {
		return $_formGUIDs.length > 0 ? $_formGUIDs[0] : undefined;
	},

	//DESTROY FORM OBJECTS

	/**
	 * @public
	 * @description
	 * clears the internal model, $_formModel, $_formSettings and formGuid - all if found
	 * @param {String} GUID
	 */
	destroyFormData: (GUID) => {
		let index = $_formGUIDs.indexOf(GUID);
		if (index !== -1) {
			$_formGUIDs.splice(index, 1);
		}		
		if ($_formSettings[GUID] != undefined) {
			$_formSettings[GUID] = undefined;
			delete $_formSettings[GUID];
		}
		if ($_formDirty[GUID] != undefined) {
			$_formDirty[GUID] = undefined;
			delete $_formDirty[GUID];
		}
		if ($_formStateModel[GUID] != undefined) {
			$_formStateModel[GUID] = undefined;
			delete $_formStateModel[GUID];
		}
		if ($_dualModeFormsState.has(GUID)) $_dualModeFormsState.delete(GUID);
	},

	//FIELD LOADING METHODS

	/**
	 * @public
	 * @description
	 * sets the form mounted state
	 * @param {String} GUID
	 * @param {String} state - defaults to 'mounted'
	 */
	setFormState(GUID, state = 'mounted') {
		$_formStateModel[GUID] = state;
	},

	/**
	 * @public
	 * @description
	 * returns the form mounted state as Boolean
	 * @returns {String}
	 */
	getFormState(GUID) {
		if ($_formStateModel[GUID] != undefined) return $_formStateModel[GUID];
		else return 'loading';
	},

	//FORM DIRTY METHODS

	/**
	 * @public
	 * @description
	 * tests whether all currently active forms are dirty and returns false only if all are clean
	 */
	isAllFormsDirty() {
		let keys = Object.keys($_formDirty);
		for (let i = keys.length - 1; i >= 0; i--) {
			if ($_formDirty[keys[i]] === true) {
				return true;
			}
		}
		return false;
	},

	/**
	 * @public
	 * @description
	 * tests whether the passed form is dirty and returns false only if it is clean
	 *
	 * @param {String} GUID
	 */
	isFormDirty(GUID) {
		return $_formDirty[GUID] != undefined ? $_formDirty[GUID] : false;
	},

	/**
	 * @public
	 * @description
	 * Do we have forms open?
	 */
	hasForms() {
		return Array.isArray($_formGUIDs) && $_formGUIDs.length > 0;
	},

	/**
	 * @public
	 * @description
	 * get the number of open forms
	 */
	getActiveFormCount() {
		if (Array.isArray($_formGUIDs)) return $_formGUIDs.length;
		else return 0;
	},

	/**
	 * @public
	 * @description
	 * get the number of open forms
	 */
	getActiveFormGuids() {
		return Array.isArray($_formGUIDs) && $_formGUIDs.length > 0 ? $_formGUIDs : [];
	},
	/**
	 * @public
	 * @description
	 * sets form dirty state
	 * @param {String} GUID
	 * @param {Boolean} state
	 */
	setFormDirtyStatus(GUID, state = true) {
		$_formDirty[GUID] = state;
	},

	/**
	 * @public
	 * @description
	 * sets form dirty state for all forms
	 * @param {Boolean} state
	 */
	setAllFormsDirtyStatus(state = false) {
		let keys = Object.keys($_formDirty);
		for (let i = keys.length - 1; i >= 0; i--) {
			$_formDirty[keys[i]] = state;
		}
	},

	//VALIDATION

	/**
	 * @public
	 * @description
	 * returns the form/subform as a linear object keyed by each section/subField/field full key ref
	 * pass method this.form or this.field in the case of repeaters to get a linear form or subform
	 * @param {Object} form
	 * @param {Object} isIncludeHiddenFields
	 * @returns {Object}
	 */
	getLinearFormObject: (form, isIncludeHiddenFields = false, isRestrictedToRepeaterBoundary = false, isRetainDeletedSubForms = false) => {
		let formGuidMap = new Map();
		let formIdMap = new Map();
		let sectionsFieldsArray = form.sections || form.fields || [];
		if (form.definition && Array.isArray(form.definition.subFields) && form.definition.fieldType === 'cs-subsection') {
			sectionsFieldsArray.push(...form.definition.subFields);
		} else if (Array.isArray(form.rows)) {
			for (let row of form.rows) {
				if (isRemoved !== true) sectionsFieldsArray.push(...row.fields);
			}
		}
		while (sectionsFieldsArray[0] !== undefined) {
			let nestedFields = [];
			for (let i = 0, formElement; (formElement = sectionsFieldsArray[i]) !== undefined; i++) {
				//loop through all visible "fields" or visible and hidden ones if the param is set and push into return object
				if (formElement.isVisible !== false || isIncludeHiddenFields === true) {
					formGuidMap.set(formElement.guid, formElement);
					if (formIdMap.has(formElement.id) === false) formIdMap.set(formElement.id, [formElement]);
					else formIdMap.get(formElement.id).push(formElement);
					if (formElement.fields) nestedFields.push(...formElement.fields);
					else {
						if (formElement.definition != undefined && Array.isArray(formElement.definition.subFields) && formElement.definition.fieldType === 'cs-subsection') {
							nestedFields.push(...formElement.definition.subFields);
						} else if (Array.isArray(formElement.rows) && isRestrictedToRepeaterBoundary === false) {
							for (let row of formElement.rows) {
								//subforms are retained for the getPayload formClass call
								if (row.isRemoved !== true || isRetainDeletedSubForms === true) nestedFields.push(...row.fields); //if the row is soft deleted don't include it
							}
						}
					}
				}
			}
			sectionsFieldsArray = nestedFields;
		}
		return {formGuidMap: formGuidMap, formIdMap: formIdMap};
	},

	/**
	 * @public
	 * @description
	 * helper method that generates a field validation record
	 * @param {Object} field
	 * @returns {Object}
	 */
	getFieldValidationRecord: (field) => {
		let fieldRecord = {
			errors: field.validationState.errors,
			fieldId: field.id,
			fieldLoopIndex: field.loopIndex,
			fieldIdRef: field.fullKeyRef, //potential unused
			fieldLabel: field.label,
			fieldDefinition: field.definition,
			fieldSectionId: field.sectionId,
			fieldSectionGuid: field.sectionGuid,
			fieldGuid: field.guid,
			fieldParentGuid: field.parentGuid,
			sectionFieldIdentifier: field.sectionFieldIdentifier
		};
		return fieldRecord;
	},

	/**
	 * @public
	 * @description
	 * returns the linked formGuid of a form in dual mode
	 * @param {String} formGuid
	 * @returns {String || Void(0)}
	 */
	getLinkedFormGuid: (formGuid) => {
		let formSettings = $_formSettings[formGuid];

		if ($cs_.get(formSettings, 'linkedForm', 'dualMode') === true) {
			let linkedId = formSettings.linkedForm.id;
			let linkedGuidKeys = Object.keys($_formSettings);
			for (let linkedGuid of linkedGuidKeys) {
				if ($_formSettings[linkedGuid].id === linkedId) return linkedGuid;
			}
		}
	},

	/**
	 * @public
	 * @description
	 * compare form with its linked form
	 * method uses a linearForm restricted to the root level - isRestrictedToRepeaterBoundary = true
	 * @param {String} formGuid
	 * @param {Object} form
	 */
	compareForms: (formGuid, form) => {
		let formSettings = $_formSettings[formGuid];

		if ($cs_.get(formSettings, 'showFormDifferences') === true) {
			let linkedFormGuid = $csForm.getLinkedFormGuid(formGuid);

			if (linkedFormGuid != undefined && form != undefined) {
				let appNode = $cs_.getAppNode();
				let linkedFormNode = $cs_.querySelectorVue(appNode, {name: 'cs-form', dataKey: 'formGuid', dataValue: linkedFormGuid});

				if (linkedFormNode != undefined) {
					//TODO rather than constructing new forms - consider using the formclass and looping the entire form ???Test speed???
					let shortLinearForm1 = $csForm.getLinearFormObject(form, false, true);
					let shortLinearForm2 = $csForm.getLinearFormObject(linkedFormNode.form, false, true);

					if (shortLinearForm1 != undefined && shortLinearForm2 != undefined) {
						//loop the formGuidMap - the formIdMap always contains an array of fields, in this case we are only interested in the first item
						shortLinearForm1.formGuidMap.forEach((formElement) => {
							let comparisonFormField = shortLinearForm2.formIdMap.get(formElement.id);

							if (Array.isArray(comparisonFormField) === true) {
								let formElement2 = comparisonFormField[0];
								let value1 = formElement.value;
								let value2 = formElement2.value;
								let isMatch = true;
								//check fieldTypes
								if (formElement.definition != undefined) {
									if (formElement2.definition == undefined) {
										isMatch = false;
									} else if (formElement.definition.fieldType !== formElement2.definition.fieldType) {
										isMatch = false;
									} else if (formElement.definition.fieldType === 'cs-select') {
										//check cs-select fields differently - value1 === value2 if both are null
										if (value1 !== value2) {
											//check both are arrays of the same length
											if (Array.isArray(value1) === false || Array.isArray(value2) === false) {
												isMatch = false;
											} else if (value1.length != value2.length) {
												isMatch = false;
											} else {
												//finally normalise the arrays to primitives and check for matching value only
												if (value1[0] != undefined && typeof value1[0] === 'object') value1 = value1.map((v) => v.value);
												if (value2[0] != undefined && typeof value2[0] === 'object') value2 = value2.map((v) => v.value);
												//loop through array1 and check array2 always has that value - on the first failure set isMatch to false and quit loop
												for (let i = 0, iLen = value1.length; i < iLen; i++) {
													if (value2.includes(value1[i]) === false) {
														isMatch = false;
														continue;
													}
												}
											}
										}
									} else {
										isMatch = $cs_.isMatch(value1, value2);
									}

									//set the match state
									if (isMatch === false) formElement.isFormComparisonDifference = true;
								}
							}
						});
					}
				}
			}
		}
	},

	/**
	 * @public
	 * @description
	 * Will find the relevant targeted field in the linked form and scroll it into
	 * view - including opening its section if needed.
	 * @param {String} formGuid
	 * @param {Object} form
	 */
	jumpToLinkedFormMatchingField: (fieldId, formGuid) => {
		let formSettings = $_formSettings[formGuid];

		if ($cs_.get(formSettings, 'showFormDifferences') === true) {
			let linkedFormGuid = $csForm.getLinkedFormGuid(formGuid);

			if (linkedFormGuid != undefined) {
				let appNode = $cs_.getAppNode();
				let linkedFormNode = $cs_.querySelectorVue(appNode, {name: 'cs-form', dataKey: 'formGuid', dataValue: linkedFormGuid});

				if (linkedFormNode != undefined) {
					let shortLinearForm2 = $csForm.getLinearFormObject(linkedFormNode.form, false, true);
					let formElementArray = shortLinearForm2.formIdMap.get(fieldId);

					//jump to the linked form field
					if (formElementArray != undefined) linkedFormNode.jumpToSection(formElementArray[0].sectionGuid, formElementArray[0].fullKeyRef, 'cs-form-dual-comparison-difference-field-target');
				}
			}
		}
	},

	/**
	 * @public
	 * @description
	 * Sets a new form state in the dual mode forms map
	 * @param { String } GUID
	 */
	setDualModeFormsState(GUID) {
		if (GUID == undefined || $_dualModeFormsState.has(GUID)) return;
		$_dualModeFormsState.set(GUID, {locked: false});
	},

	/**
	 * @public
	 * @description
	 * Gets a form state from the dual mode forms map
	 * @param { String } GUID
	 */
	getDualModeFormsState(GUID) {
		return $_dualModeFormsState.get(GUID);
	},

	/**
	 * @public
	 * @description
	 * Updates current form state value object in the dual mode forms map
	 * @param { String } GUID
	 * @param { Any } stateValue
	 * @param { String } stateKey
	 * @returns
	 */
	updateDualModeFormsState(GUID, stateValue, stateKey) {
		if (GUID == undefined) return;
		let stateObj = stateValue;
		if (typeof stateObj != 'object') {
			stateObj = {};
			if (typeof stateKey == 'string') stateObj[stateKey] = stateValue;
		}
		$_dualModeFormsState.set(GUID, stateObj);
	},

	/**
	 * @public
	 * @description
	 * Removes a form state the dual mode forms map
	 * @param { String } GUID
	 * @returns
	 */
	deleteDualModeFormsState(GUID) {
		$_dualModeFormsState.delete(GUID);
	},

	/**
	 * @public
	 * @description
	 * Returns a dual mode forms map
	 */
	getDualModeFormsStateMap() {
		return $_dualModeFormsState ? $_dualModeFormsState : new Map();
	}	
};

//DEFAULT EXPORT
export {$csForm as default};
